import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ContactSection from '../../components/ContactSection';
import Icon from '../../components/AppIcon';
import { generateRouteData } from '../../data/oneWayPricingData';

const OneWayBooking = () => {
  const { route } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [routeData, setRouteData] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const isRoundTrip = location?.state?.isRoundTrip || false;

  useEffect(() => {
    window.scrollTo(0, 0);

    // Parse route parameter
    const routeParts = route?.split('-to-');
    if (routeParts?.length === 2) {
      const fromCity = routeParts?.[0]?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
      const toCity = routeParts?.[1]?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
      
      const data = generateRouteData(fromCity, toCity);
      
      // Double prices for round trip
      if (isRoundTrip) {
        data.vehicles = data?.vehicles?.map(vehicle => ({
          ...vehicle,
          price: vehicle?.price * 2
        }));
      }
      
      setRouteData(data);
    }
  }, [route, isRoundTrip]);

  const handleBookNow = (vehicle) => {
    navigate('/car-details', {
      state: {
        from: routeData?.from,
        to: routeData?.to,
        vehicle: vehicle?.name,
        price: vehicle?.price,
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        image: vehicle?.image,
        alt: vehicle?.alt,
        serviceType: isRoundTrip ? 'Round Trip' : 'One Way',
        date: location?.state?.date,
        time: location?.state?.time,
        returnDate: location?.state?.returnDate,
        distance: routeData?.distance,
        toll: routeData?.toll
      }
    });
  };

  if (!routeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
          <p className="text-gray-600">Loading route details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${routeData?.from} to ${routeData?.to} ${isRoundTrip ? 'Round Trip' : 'One Way'} Taxi Services | Advait Tour and Travels`}
        description={`Book ${routeData?.from} to ${routeData?.to} ${isRoundTrip ? 'round trip' : 'one way'} taxi with Advait Tour and Travels. Choose from Maruti Dzire, Brezza, Ertiga, Kia Carens, Innova Crysta. Best rates starting ₹${routeData?.vehicles?.[0]?.price}/-`}
        keywords={`${routeData?.from} to ${routeData?.to} taxi, ${isRoundTrip ? 'round trip' : 'one way'} cab, ${routeData?.from} ${routeData?.to} car rental, Advait Tour and Travels`}
        ogTitle={`${routeData?.from} to ${routeData?.to} ${isRoundTrip ? 'Round Trip' : 'One Way'} Taxi Services | Advait Tour and Travels`}
        ogDescription={`Book ${routeData?.from} to ${routeData?.to} ${isRoundTrip ? 'round trip' : 'one way'} taxi with Advait Tour and Travels. Choose from Maruti Dzire, Brezza, Ertiga, Kia Carens, Innova Crysta. Best rates starting ₹${routeData?.vehicles?.[0]?.price}/-`}
        ogUrl={`https://advaittourandtravels.com/one-way-booking/${route}`}
        canonicalUrl={`https://advaittourandtravels.com/one-way-booking/${route}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TaxiService",
          "name": "Advait Tour and Travels",
          "serviceType": `${isRoundTrip ? 'Round Trip' : 'One Way'} Taxi Service`,
          "areaServed": {
            "@type": "Place",
            "name": `${routeData?.from} to ${routeData?.to}`
          },
          "priceRange": `₹${routeData?.vehicles?.[0]?.price}`
        }}
      />

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {routeData?.from} to {routeData?.to} One Way Taxi Services
              </h1>
            </div>
          </div>
        </section>

        {/* Vehicle Options */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {routeData?.vehicles?.map((vehicle, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                    {/* Vehicle Image */}
                    <div className="w-full md:w-64 h-48 flex-shrink-0">
                      <img
                        src={vehicle?.image}
                        alt={vehicle?.alt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Vehicle Details */}
                    <div className="flex-1 w-full">
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 uppercase mb-1">Car Type</p>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {vehicle?.name}
                        </h3>
                      </div>
                      
                      <div className="flex items-center gap-8 mb-4">
                        <div className="flex flex-col items-center">
                          <Icon name="Users" size={24} className="text-blue-600 mb-1" />
                          <span className="text-sm text-gray-600">{vehicle?.seats} Seats</span>
                        </div>
                        <div className="flex flex-col items-center">
                          <Icon name="Luggage" size={24} className="text-blue-600 mb-1" />
                          <span className="text-sm text-gray-600">{vehicle?.luggage} Luggage</span>
                        </div>
                      </div>
                    </div>

                    {/* Price and Button */}
                    <div className="flex flex-col items-center gap-4 w-full md:w-auto">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600">
                          ₹{vehicle?.price?.toLocaleString('en-IN')}/-
                        </div>
                        <div className="text-sm text-gray-500">Inc. of Taxes*</div>
                      </div>
                      
                      <button
                        onClick={() => handleBookNow(vehicle)}
                        className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg transition-colors duration-300 whitespace-nowrap"
                      >
                        Select Car
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Choose Advait Tour and Travels?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={32} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Safe & Secure</h3>
                <p className="text-gray-600">Verified drivers and well-maintained vehicles</p>
              </div>
              
              <div className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Clock" size={32} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">24/7 Available</h3>
                <p className="text-gray-600">Round the clock service for your convenience</p>
              </div>
              
              <div className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="IndianRupee" size={32} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Best Prices</h3>
                <p className="text-gray-600">Competitive rates with no hidden charges</p>
              </div>
              
              <div className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Star" size={32} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Professional Drivers</h3>
                <p className="text-gray-600">Experienced and courteous chauffeurs</p>
              </div>
            </div>
          </div>
        </section>

        <ContactSection />
        <Footer />
      </div>
    </>
  );
};

export default OneWayBooking;